import { useState } from "react";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APPLE_JS = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const loadAppleJs = () => new Promise((resolve, reject) => {
  if (window.AppleID) return resolve();
  const s = document.createElement("script");
  s.src = APPLE_JS; s.async = true;
  s.onload = resolve; s.onerror = () => reject(new Error("Apple sign-in is unavailable"));
  document.head.appendChild(s);
});

// useGoogleLogin throws when the client ID is missing (e.g. a production build without
// REACT_APP_GOOGLE_CLIENT_ID baked in), so this hook must only run inside a component
// that is rendered conditionally — never call it unconditionally in SocialAuth.
const GoogleButton = () => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (resp) => {
      setBusy(true);
      try {
        await loginWithGoogle(resp.code);
        navigate("/dashboard");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Google sign-in failed. Please try again.");
      } finally { setBusy(false); }
    },
    onError: () => toast.error("Google sign-in was cancelled."),
  });

  return (
    <button type="button" className="social-btn" data-testid="google-login-btn"
            disabled={busy} onClick={() => googleLogin()}>
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
      {busy ? "Signing in…" : "Google"}
    </button>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const SocialAuth = ({ label = "or continue with" }) => {
  const { loginWithApple } = useAuth();
  const navigate = useNavigate();
  const [appleBusy, setAppleBusy] = useState(false);
  const googleEnabled = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const appleLogin = async () => {
    setAppleBusy(true);
    try {
      await loadAppleJs();
      const start = await axios.get(`${API}/auth/apple/start`, { withCredentials: true });
      const { state, nonce, redirect_uri, client_id } = start.data;
      window.AppleID.auth.init({ clientId: client_id, scope: "name email", redirectURI: redirect_uri, state, nonce, usePopup: true });
      const result = await window.AppleID.auth.signIn();
      const code = result.authorization?.code;
      const retState = result.authorization?.state;
      if (!code || retState !== state) throw new Error("Invalid Apple response");
      const name = [result.user?.name?.firstName, result.user?.name?.lastName].filter(Boolean).join(" ") || undefined;
      await loginWithApple(code, retState, name);
      navigate("/dashboard");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" && d) || (e.error === "popup_closed_by_user" ? "Apple sign-in was cancelled." : "Apple sign-in failed. Please try again."));
    } finally { setAppleBusy(false); }
  };

  return (
    <div className="social-auth" data-testid="social-auth">
      <div className="social-divider"><span>{label}</span></div>
      <div className="social-btns">
        {googleEnabled && <GoogleButton />}
        <button type="button" className="social-btn" data-testid="apple-login-btn"
                disabled={appleBusy} onClick={appleLogin} title="Sign in with Apple">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M16.37 1.43c.06 1.03-.33 2.04-1.01 2.78-.69.76-1.82 1.35-2.88 1.26-.08-1 .37-2.05 1.01-2.74.72-.78 1.94-1.35 2.88-1.3zM20.5 17.1c-.57 1.32-.85 1.9-1.58 3.07-1.03 1.63-2.48 3.66-4.28 3.68-1.6.02-2.01-1.04-4.18-1.03-2.17.01-2.62 1.05-4.22 1.03-1.8-.02-3.17-1.86-4.2-3.49C-.6 16.22-.9 10.9 1.28 8.06 2.34 6.68 3.98 5.8 5.56 5.8c1.6 0 2.6 1.05 3.93 1.05 1.28 0 2.06-1.05 3.92-1.05 1.4 0 2.88.76 3.94 2.08-3.46 1.9-2.9 6.84.65 8.22z"/>
          </svg>
          {appleBusy ? "Signing in…" : "Apple"}
        </button>
      </div>
    </div>
  );
};

export default SocialAuth;
